import React, { useState, useEffect, useMemo } from 'react';
import { stakeholdersAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Users,
  UserCheck,
  Clock,
  CheckCircle,
  AlertCircle,
  LogIn,
  User,
  UsersRound,
  BarChart3,
  ChevronDown,
  ChevronRight,
  X
} from 'lucide-react';

// Activity status configuration - shortened labels
const activityConfig = {
  never_logged_in: {
    label: 'Not Started',
    bg: 'bg-slate-100',
    text: 'text-slate-700',
    icon: AlertCircle
  },
  started_about_me: {
    label: 'Started',
    bg: 'bg-amber-100',
    text: 'text-amber-700',
    icon: Clock
  },
  completed_about_me: {
    label: 'Complete',
    bg: 'bg-emerald-100',
    text: 'text-emerald-700',
    icon: CheckCircle
  }
};

// Segment Completion Summary Table Component with clickable rows
function SegmentCompletionTable({ data, selectedSegment, onSegmentSelect }) {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-4 text-slate-500">
        <BarChart3 className="h-6 w-6 mx-auto text-slate-300 mb-1" />
        <p className="text-xs">No segment data available</p>
      </div>
    );
  }
  
  // Group by segment type
  const groupedData = data.reduce((acc, item) => {
    const typeName = item.segment_type_name;
    if (!acc[typeName]) {
      acc[typeName] = [];
    }
    acc[typeName].push(item);
    return acc;
  }, {});
  
  return (
    <div className="space-y-3">
      {Object.entries(groupedData).map(([typeName, segments]) => (
        <div key={typeName}>
          <h4 className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 px-1">
            {typeName}
          </h4>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-[150px] text-[10px] py-1">Segment</TableHead>
                <TableHead className="w-[50px] text-[10px] text-center py-1">Users</TableHead>
                <TableHead className="w-[90px] text-[10px] text-center py-1">About Me %</TableHead>
                <TableHead className="w-[70px] text-[10px] text-center py-1">Avg Map</TableHead>
                <TableHead className="w-[90px] text-[10px] text-center py-1">About Others %</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {segments.map((seg) => {
                const isSelected = selectedSegment?.segment_id === seg.segment_id;
                const isUncategorized = seg.is_uncategorized;
                return (
                  <TableRow 
                    key={seg.segment_id}
                    className={`cursor-pointer transition-colors ${
                      isSelected ? 'bg-[#5AA2AE]/10 ring-1 ring-[#5AA2AE]' : 
                      isUncategorized ? 'bg-amber-50/50 hover:bg-amber-50' : 'hover:bg-slate-50'
                    }`}
                    onClick={() => onSegmentSelect(isSelected ? null : seg)}
                  >
                    <TableCell className={`font-medium text-xs py-1 ${isUncategorized ? 'text-amber-700 italic' : ''}`}>
                      {seg.segment_name}
                    </TableCell>
                    <TableCell className="text-center py-1">
                      <Badge variant="outline" className={`text-[9px] ${isUncategorized ? 'border-amber-300 text-amber-600' : ''}`}>
                        {seg.user_count}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-1">
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="text-[10px] font-medium">{seg.avg_about_me}%</span>
                        <Progress value={seg.avg_about_me} className="h-1 w-12" />
                      </div>
                    </TableCell>
                    <TableCell className="text-center py-1">
                      <span className="text-xs font-medium">{seg.avg_mappings}</span>
                    </TableCell>
                    <TableCell className="py-1">
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="text-[10px] font-medium">{seg.avg_about_others}%</span>
                        <Progress value={seg.avg_about_others} className="h-1 w-12" />
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      ))}
    </div>
  );
}

export default function InvitedUsersSection({ projectId }) {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [segmentSectionOpen, setSegmentSectionOpen] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState(null);
  
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await stakeholdersAPI.getInvitedStats(projectId);
        setStats(response.data);
      } catch (err) {
        console.error('Failed to fetch invited stats:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchStats();
  }, [projectId]);
  
  // Extract unique segment types from users for dynamic columns
  const segmentTypes = useMemo(() => {
    if (!stats?.users) return [];
    const typeMap = new Map();
    stats.users.forEach(u => {
      (u.segments || []).forEach(seg => {
        if (!typeMap.has(seg.segment_type_id)) {
          typeMap.set(seg.segment_type_id, seg.segment_type_name);
        }
      });
    });
    return Array.from(typeMap.entries()).map(([id, name]) => ({ id, name }));
  }, [stats?.users]);
  
  // Filter users by activity status and segment
  const filteredUsers = useMemo(() => {
    let users = stats?.users || [];
    
    // Filter by activity status
    if (activeTab !== 'all') {
      users = users.filter(u => u.activity_status === activeTab);
    }
    
    // Filter by selected segment
    if (selectedSegment) {
      if (selectedSegment.is_uncategorized) {
        // For "Uncategorized", show users who DON'T have this segment type
        users = users.filter(u => 
          !(u.segments || []).some(seg => seg.segment_type_id === selectedSegment.segment_type_id)
        );
      } else {
        // Normal segment filter
        users = users.filter(u => 
          (u.segments || []).some(seg => seg.segment_id === selectedSegment.segment_id)
        );
      }
    }
    
    return users;
  }, [stats?.users, activeTab, selectedSegment]);
  
  // Helper to get segment value for a user - prioritizes the selected segment if it matches
  const getSegmentValue = (user, segmentTypeId) => {
    const userSegments = (user.segments || []).filter(s => s.segment_type_id === segmentTypeId);
    if (userSegments.length === 0) return '—';
    
    // If we have a selected segment and it's of this type, prioritize showing it
    if (selectedSegment && selectedSegment.segment_type_id === segmentTypeId) {
      const matchingSeg = userSegments.find(s => s.segment_id === selectedSegment.segment_id);
      if (matchingSeg) return matchingSeg.segment_name;
    }
    
    // If multiple segments of same type, join them
    if (userSegments.length > 1) {
      return userSegments.map(s => s.segment_name).join(', ');
    }
    
    return userSegments[0].segment_name;
  };
  
  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card 
          className={`cursor-pointer transition-all ${activeTab === 'all' && !selectedSegment ? 'ring-2 ring-[#5AA2AE]' : ''}`}
          onClick={() => { setActiveTab('all'); setSelectedSegment(null); }}
        >
          <CardContent className="pt-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-2xl font-bold">{stats?.total_invited || 0}</p>
                <p className="text-xs text-slate-500 mt-1">Total Invited</p>
              </div>
              <div className="p-2 rounded-lg bg-blue-100">
                <Users className="h-4 w-4 text-blue-700" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`cursor-pointer transition-all ${activeTab === 'never_logged_in' ? 'ring-2 ring-[#5AA2AE]' : ''}`}
          onClick={() => setActiveTab(activeTab === 'never_logged_in' ? 'all' : 'never_logged_in')}
        >
          <CardContent className="pt-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-2xl font-bold">{stats?.never_logged_in || 0}</p>
                <p className="text-xs text-slate-500 mt-1">Not Started</p>
              </div>
              <div className="p-2 rounded-lg bg-slate-100">
                <AlertCircle className="h-4 w-4 text-slate-700" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`cursor-pointer transition-all ${activeTab === 'started_about_me' ? 'ring-2 ring-[#5AA2AE]' : ''}`}
          onClick={() => setActiveTab(activeTab === 'started_about_me' ? 'all' : 'started_about_me')}
        >
          <CardContent className="pt-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-2xl font-bold">{stats?.started_about_me || 0}</p>
                <p className="text-xs text-slate-500 mt-1">Started</p>
              </div>
              <div className="p-2 rounded-lg bg-amber-100">
                <Clock className="h-4 w-4 text-amber-700" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className={`cursor-pointer transition-all ${activeTab === 'completed_about_me' ? 'ring-2 ring-[#5AA2AE]' : ''}`}
          onClick={() => setActiveTab(activeTab === 'completed_about_me' ? 'all' : 'completed_about_me')}
        >
          <CardContent className="pt-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-2xl font-bold">{stats?.completed_about_me || 0}</p>
                <p className="text-xs text-slate-500 mt-1">Complete</p>
              </div>
              <div className="p-2 rounded-lg bg-emerald-100">
                <CheckCircle className="h-4 w-4 text-emerald-700" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Segment Completion Summary - Collapsible */}
      <Collapsible open={segmentSectionOpen} onOpenChange={setSegmentSectionOpen}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="pb-2 cursor-pointer hover:bg-slate-50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {segmentSectionOpen ? (
                    <ChevronDown className="h-4 w-4 text-slate-500" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-slate-500" />
                  )}
                  <BarChart3 className="h-4 w-4 text-[#5AA2AE]" />
                  <CardTitle className="text-sm">Completion Rates by Segment</CardTitle>
                </div>
                <CardDescription className="text-[10px]">
                  Click to {segmentSectionOpen ? 'collapse' : 'expand'}
                </CardDescription>
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0">
              <p className="text-[10px] text-slate-500 mb-2">Click a segment row to filter the list below</p>
              <SegmentCompletionTable 
                data={stats?.segment_summary || []} 
                selectedSegment={selectedSegment}
                onSegmentSelect={setSelectedSegment}
              />
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
      
      {/* Users Table */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Invited Users Activity</CardTitle>
              <CardDescription className="text-xs">
                {activeTab === 'all' && !selectedSegment
                  ? 'Click on a status card or segment to filter' 
                  : `Showing ${filteredUsers.length} users`
                }
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {selectedSegment && (
                <Badge 
                  className="cursor-pointer flex items-center gap-1" 
                  variant="secondary"
                  onClick={() => setSelectedSegment(null)}
                >
                  {selectedSegment.segment_name}
                  <X className="h-3 w-3" />
                </Badge>
              )}
              {(activeTab !== 'all' || selectedSegment) && (
                <Badge 
                  className="cursor-pointer" 
                  variant="outline"
                  onClick={() => { setActiveTab('all'); setSelectedSegment(null); }}
                >
                  Clear All
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredUsers.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Users className="h-12 w-12 mx-auto text-slate-300 mb-3" />
              <p>No invited users match the current filter</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[120px] text-[10px]">Name</TableHead>
                    {segmentTypes.map(st => (
                      <TableHead key={st.id} className="w-[80px] text-[10px] text-center">
                        {st.name}
                      </TableHead>
                    ))}
                    <TableHead className="w-[70px] text-[10px]">Invitation</TableHead>
                    <TableHead className="w-[80px] text-[10px]">Status</TableHead>
                    <TableHead className="w-[70px] text-[10px] text-center">
                      <div className="flex flex-col items-center">
                        <User className="h-3 w-3" />
                        <span>About Me</span>
                      </div>
                    </TableHead>
                    <TableHead className="w-[50px] text-[10px] text-center">
                      <div className="flex flex-col items-center">
                        <UsersRound className="h-3 w-3" />
                        <span>Maps</span>
                      </div>
                    </TableHead>
                    <TableHead className="w-[70px] text-[10px] text-center">
                      <div className="flex flex-col items-center">
                        <UsersRound className="h-3 w-3" />
                        <span>Others</span>
                      </div>
                    </TableHead>
                    <TableHead className="w-[50px] text-[10px] text-right">Days</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map(u => {
                    const actConfig = activityConfig[u.activity_status] || activityConfig.never_logged_in;
                    const ActivityIcon = actConfig.icon;
                    
                    return (
                      <TableRow key={u.id}>
                        <TableCell className="font-medium text-xs py-1">
                          {u.first_name} {u.last_name}
                        </TableCell>
                        {segmentTypes.map(st => (
                          <TableCell key={st.id} className="text-[9px] text-center py-1 text-slate-600">
                            {getSegmentValue(u, st.id)}
                          </TableCell>
                        ))}
                        <TableCell className="py-1">
                          <Badge variant="outline" className="capitalize text-[9px] whitespace-nowrap">
                            {u.invitation_status}
                          </Badge>
                        </TableCell>
                        <TableCell className="py-1">
                          <Badge className={`${actConfig.bg} ${actConfig.text} text-[9px] whitespace-nowrap`}>
                            <ActivityIcon className="h-2.5 w-2.5 mr-0.5" />
                            {actConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="py-1">
                          <div className="flex flex-col items-center gap-0.5">
                            <span className="text-[10px] font-medium">{u.about_me_progress || 0}%</span>
                            <Progress 
                              value={u.about_me_progress || 0} 
                              className="h-1 w-12"
                            />
                          </div>
                        </TableCell>
                        <TableCell className="text-center py-1">
                          <Badge variant="outline" className="text-[9px]">
                            {u.about_others_count || 0}
                          </Badge>
                        </TableCell>
                        <TableCell className="py-1">
                          <div className="flex flex-col items-center gap-0.5">
                            <span className="text-[10px] font-medium">{u.about_others_progress || 0}%</span>
                            <Progress 
                              value={u.about_others_progress || 0} 
                              className="h-1 w-12"
                            />
                          </div>
                        </TableCell>
                        <TableCell className="text-right py-1">
                          {u.days_since_login !== null ? (
                            <span className={`text-[10px] ${u.days_since_login > 7 ? 'text-amber-600' : 'text-slate-600'}`}>
                              {u.days_since_login}
                            </span>
                          ) : (
                            <span className="text-[10px] text-slate-400">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
